import { areIntervalsOverlapping } from "date-fns";
import { autorun, makeAutoObservable } from "mobx";

import { RuleAttributes, RuleFragment, RuleKind } from "@/gql/graphql";
import { formatISODate, parseDate } from "@/utils/datetime";
import { apply } from "@/utils/functions";

import Interval from "./Interval";
import Schedule from "./Schedule";

export default class Rule {
  id?: string;
  key?: string;
  date?: Date;
  ruleType: RuleKind;
  wday?: string | null;
  intervals: Interval[] = [];
  _destroy?: boolean;

  schedule: Schedule;

  get isValid(): boolean {
    //exclude destroyed intervals from validation
    return this.presentIntervals.every((rule) => rule.isValid);
  }

  constructor(schedule: Schedule, ruleType: RuleKind) {
    makeAutoObservable(this);
    this.ruleType = ruleType;
    this.schedule = schedule;
    autorun(() => {
      this.areIntervalsOverlapping();
    });
  }

  get presentIntervals() {
    return this.intervals
      .filter((i) => !i._destroy)
      .sort((a, b) => {
        // Sort by start time for easy scanning.
        // If start time and end time is not both provided, don't sort yet.
        if (!a.interval || !b.interval) return 0;

        const aTime = a.interval.start.getTime();
        const bTime = b.interval.start.getTime();

        if (aTime > bTime) return 1;
        else return -1;
      });
  }

  get checked() {
    return this.presentIntervals.length > 0;
  }

  remove = () => {
    // Only override dates can be deleted.
    if (this.ruleType !== RuleKind.Date) return;

    if (this.id) this._destroy = true;
    else this.schedule.removeOverrideRule(this);
  };

  setDate = (date: Date | null) => {
    this.date = date ?? undefined;
  };

  areIntervalsOverlapping = () => {
    this.intervals.forEach((interval) => {
      if (!interval.interval) return;
      const hasOverlap = this.intervals.some((inter) => {
        if (inter._destroy) return false;

        if (inter === interval) return false;

        if (!inter.interval || !interval.interval) return false;

        return areIntervalsOverlapping(inter.interval, interval.interval);
      });
      interval.errorMessage = hasOverlap
        ? "this interval overlaps with another interval"
        : "";
    });
  };

  removeInterval = (interval: Interval) => {
    if (interval?.id != null) {
      interval._destroy = true;
      interval.errorMessage = "";
    } else {
      this.intervals.splice(this.intervals.indexOf(interval), 1);
    }
  };

  removeIntervals = () => {
    this.intervals.forEach((int) => this.removeInterval(int));
  };

  restoreIntervals = () => {
    this.intervals.forEach((int) => int.restore());
  };

  addInterval = () => {
    this.intervals.push(new Interval());
  };

  setIntervals = (newIntervals: Interval[]) => {
    this.intervals = newIntervals;
  };

  toAttributes = (): RuleAttributes => {
    return {
      id: this.id,
      date: apply(this.date, formatISODate),
      ruleType: this.ruleType as RuleKind,
      wday: this.wday,
      _destroy: this._destroy,
    };
  };

  static fromFragment(schedule: Schedule, fragment: RuleFragment) {
    const newRule = new Rule(schedule, fragment.ruleType);
    newRule.date = apply(fragment.date, parseDate);
    newRule.wday = fragment.wday;
    newRule.id = fragment.id;
    newRule.intervals = fragment.intervals.map((interval) =>
      Interval.fromFragment(interval)
    );

    return newRule;
  }

  static wday(schedule: Schedule, wday: string) {
    const newRule = new Rule(schedule, RuleKind.Wday);
    newRule.wday = wday;
    return newRule;
  }
}
